import { gql } from "@apollo/client";
import { client } from "../../../../graphql/client";

export const SUBMIT_FEEDBACK = gql`
    mutation SubmitFeedback($input: SubmitFeedbackInput!) {
        submitFeedback(input: $input)
    }
`;

export const CANCEL_FEEDBACK = gql`
    mutation CancelFeedback($input: CancelFeedbackInput!) {
        cancelFeedback(input: $input)
    }
`;

export const MARK_VALIDATION_POPUP_SHOWN = gql`
    mutation MarkValidationPopupShown($input: MarkValidationPopupShownInput!) {
        markValidationPopupShown(input: $input)
    }
`;

export interface AccessCodeInput {
    id: number;
    codeType: string;
    accessCode: string | null;
    status?: number | null; // 状态枚举值：0-待验证，1-可用，2-不可用（从getAccessCodesByOrderId获取，必填）
}

export interface SubmitFeedbackInput {
    driverAccountId: number;
    isValidFeedback: boolean;
    latitude?: number | null;
    longitude?: number | null;
    accessCode: AccessCodeInput;
    fromPopup?: boolean | null; // 是否从弹窗反馈：true-从弹窗反馈，false/null-从详情页反馈
}

export interface CancelFeedbackInput {
    driverAccountId: number;
    accessCode: AccessCodeInput;
}

export interface MarkValidationPopupShownInput {
    accessCodeId: number;
    driverAccountId: number;
}

export interface SubmitFeedbackResponse {
    submitFeedback: boolean;
}

export interface CancelFeedbackResponse {
    cancelFeedback: boolean;
}

export interface MarkValidationPopupShownResponse {
    markValidationPopupShown: boolean;
}

export interface SubmitFeedbackVariables {
    input: SubmitFeedbackInput;
}

export interface CancelFeedbackVariables {
    input: CancelFeedbackInput;
}

export interface MarkValidationPopupShownVariables {
    input: MarkValidationPopupShownInput;
}

export const submitFeedback = async (input: SubmitFeedbackInput): Promise<boolean> => {
    const { data } = await client.mutate<SubmitFeedbackResponse, SubmitFeedbackVariables>({
        mutation: SUBMIT_FEEDBACK,
        variables: { input },
    });
    return data?.submitFeedback || false;
};

export const cancelFeedback = async (input: CancelFeedbackInput): Promise<boolean> => {
    const { data } = await client.mutate<CancelFeedbackResponse, CancelFeedbackVariables>({
        mutation: CANCEL_FEEDBACK,
        variables: { input },
    });
    return data?.cancelFeedback || false;
};

export const markValidationPopupShown = async (input: MarkValidationPopupShownInput): Promise<boolean> => {
    const { data } = await client.mutate<MarkValidationPopupShownResponse, MarkValidationPopupShownVariables>({
        mutation: MARK_VALIDATION_POPUP_SHOWN,
        variables: { input },
    });
    return data?.markValidationPopupShown || false;
};

export const CREATE_ACCESS_CODE = gql`
    mutation CreateAccessCode($input: CreateAccessCodeInput!) {
        createAccessCode(input: $input) {
            success
            resultType
        }
    }
`;

export const UPDATE_ACCESS_CODE = gql`
    mutation UpdateAccessCode($input: UpdateAccessCodeInput!) {
        updateAccessCode(input: $input) {
            success
            resultType
        }
    }
`;

export interface CreateAccessCodeInput {
    orderId: number;
    codeType: string;
    accessCode: string;
    latitude?: number | null;
    longitude?: number | null;
}

export interface UpdateAccessCodeInput {
    orderId?: number | null; // 订单ID（用于数据重复性校验）
    accessCode: AccessCodeInput; // accessCode.accessCode 字段为新的门禁码值
    latitude?: number | null;
    longitude?: number | null;
}

export interface AccessCodeOperationResult {
    success: boolean;
    resultType: string; // "created" | "updated" | "duplicate_handled"
}

export interface CreateAccessCodeResponse {
    createAccessCode: AccessCodeOperationResult;
}

export interface UpdateAccessCodeResponse {
    updateAccessCode: AccessCodeOperationResult;
}

export interface CreateAccessCodeVariables {
    input: CreateAccessCodeInput;
}

export interface UpdateAccessCodeVariables {
    input: UpdateAccessCodeInput;
}

export const createAccessCode = async (input: CreateAccessCodeInput): Promise<AccessCodeOperationResult> => {
    const { data } = await client.mutate<CreateAccessCodeResponse, CreateAccessCodeVariables>({
        mutation: CREATE_ACCESS_CODE,
        variables: { input },
    });
    return data?.createAccessCode || { success: false, resultType: 'error' };
};

export const updateAccessCode = async (input: UpdateAccessCodeInput): Promise<AccessCodeOperationResult> => {
    const { data } = await client.mutate<UpdateAccessCodeResponse, UpdateAccessCodeVariables>({
        mutation: UPDATE_ACCESS_CODE,
        variables: { input },
    });
    return data?.updateAccessCode || { success: false, resultType: 'error' };
};

